import {
	Badge,
	Button,
	Flex,
	Select,
	Spinner,
	Table,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tooltip,
	Tr,
	useColorModeValue
} from '@chakra-ui/react';
import { format, parseISO } from 'date-fns';
import { useEffect, useState } from 'react';
import Card from '../../../components/card/Card';
import { LiveLeadDeliveryStatus } from '../../../models/liveLead.model';
import { fetchLiveLeadDeliveries } from '../../../store/features/liveLead/liveLead.slice';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { RootState } from '../../../store/store';

const STATUS_LABEL: Record<LiveLeadDeliveryStatus, { label: string; color: string }> = {
	PENDING: { label: 'Na fila', color: 'blue' },
	SENT: { label: 'Entregue', color: 'green' },
	FAILED: { label: 'Falhou', color: 'red' },
	SKIPPED: { label: 'Ignorado', color: 'gray' }
};

const LIMIT = 20;
const dt = (value?: string | null) => (value ? format(parseISO(value), 'dd/MM/yyyy HH:mm') : '—');

// As datas do lead chegam como ISO sem fuso ("1988-07-01T00:00:00"): só a
// parte da data interessa, e converter com fuso deslocaria um dia.
const dia = (value?: string | null) => (value ? value.slice(0, 10).split('-').reverse().join('/') : '—');

const brl = (value?: number | null) =>
	value === null || value === undefined
		? '—'
		: value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** Histórico do que já foi (ou vai ser) entregue nesta conta. */
export default function EntregasTable() {
	const dispatch = useAppDispatch();
	const { deliveries, deliveriesTotal, loadingDeliveries } = useAppSelector(
		(state: RootState) => state.liveLead
	);
	const textColor = useColorModeValue('navy.700', 'white');
	const subColor = useColorModeValue('secondaryGray.700', 'secondaryGray.500');

	const [page, setPage] = useState(1);
	const [status, setStatus] = useState('');

	useEffect(() => {
		dispatch(fetchLiveLeadDeliveries({ page, limit: LIMIT, status: status || undefined }));
	}, [dispatch, page, status]);

	const lastPage = Math.max(Math.ceil(deliveriesTotal / LIMIT), 1);

	return (
		<Card p='20px'>
			<Flex justify='space-between' align='center' mb='16px' gap={3} wrap='wrap'>
				<Text fontSize='lg' fontWeight='bold' color={textColor}>
					Entregas
				</Text>
				<Select
					size='sm'
					maxW='200px'
					value={status}
					onChange={e => {
						setStatus(e.target.value);
						setPage(1);
					}}
				>
					<option value=''>Todos os status</option>
					<option value='SENT'>Entregues</option>
					<option value='PENDING'>Na fila</option>
					<option value='FAILED'>Com falha</option>
					<option value='SKIPPED'>Ignorados</option>
				</Select>
			</Flex>

			{loadingDeliveries ? (
				<Flex justify='center' py='30px'>
					<Spinner />
				</Flex>
			) : deliveries.length === 0 ? (
				<Text fontSize='sm' color={subColor}>
					Nenhuma entrega ainda.
				</Text>
			) : (
				<Table size='sm' variant='simple'>
					<Thead>
						<Tr>
							<Th>CPF</Th>
							<Th>Nome</Th>
							<Th>Telefone</Th>
							<Th isNumeric>Margem</Th>
							<Th isNumeric>Solicitado</Th>
							<Th isNumeric>Parcelas</Th>
							<Th>Matrícula</Th>
							<Th>CNPJ Empregador</Th>
							<Th>Nascimento</Th>
							<Th>Admissão</Th>
							<Th>Bancos</Th>
							<Th>Higienizado</Th>
							<Th>Status</Th>
							<Th>Entregue em</Th>
						</Tr>
					</Thead>
					<Tbody>
						{deliveries.map(row => {
							const d = row.lead?.data || {};
							return (
							<Tr key={row.id}>
								<Td>{row.lead?.cpf}</Td>
								<Td>{row.lead?.name || '—'}</Td>
								<Td>{row.lead?.phone || '—'}</Td>
								<Td isNumeric>{brl(d.margemDisponivel)}</Td>
								<Td isNumeric>{brl(d.valorLiberado)}</Td>
								<Td isNumeric>{d.nroParcelas ?? '—'}</Td>
								<Td>{d.matricula || '—'}</Td>
								<Td>{d.cnpjEmpregador || '—'}</Td>
								<Td>{dia(d.dataNascimento)}</Td>
								<Td>{dia(d.dataAdmissao)}</Td>
								<Td>{(d.bancosElegiveis || []).join(', ') || '—'}</Td>
								<Td>
									<Badge colorScheme={row.lead?.enriched ? 'green' : 'gray'} borderRadius='full'>
										{row.lead?.enriched ? 'Sim' : 'Não'}
									</Badge>
								</Td>
								<Td>
									<Tooltip label={row.lastError || ''} isDisabled={!row.lastError}>
										<Badge colorScheme={STATUS_LABEL[row.status].color} borderRadius='full'>
											{STATUS_LABEL[row.status].label}
										</Badge>
									</Tooltip>
								</Td>
								<Td>{row.status === 'SENT' ? dt(row.sentAt) : dt(row.releaseAt)}</Td>
							</Tr>
							);
						})}
					</Tbody>
				</Table>
			)}

			<Flex justify='space-between' align='center' mt='16px'>
				<Text fontSize='sm' color={subColor}>
					{deliveriesTotal.toLocaleString('pt-BR')} registro(s)
				</Text>
				<Flex gap={2}>
					<Button size='sm' isDisabled={page <= 1} onClick={() => setPage(p => p - 1)}>
						Anterior
					</Button>
					<Button size='sm' isDisabled={page >= lastPage} onClick={() => setPage(p => p + 1)}>
						Próxima
					</Button>
				</Flex>
			</Flex>
		</Card>
	);
}
